import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Youtube, Facebook } from 'lucide-react';
import { useFeatureFlag } from '../context/FeatureFlagsContext';

// Custom X (formerly Twitter) brand icon — lucide-react doesn't ship the post-2023 X logo
const XIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const NAV_LINKS = [
  { label: 'Our Story', to: '/story' },
  { label: 'Roadmap', to: '/roadmap' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Strength', to: '/strength' },
  { label: 'Endurance', to: '/endurance' },
  { label: 'Combatives', to: '/training' },
  { label: 'Nutrition', to: '/nutrition' },
  { label: 'Library', to: '/library' },
  { label: 'Impact (IronLimb)', to: '/impact' },
];

const Footer = () => {
  const { enabled: merchEnabled } = useFeatureFlag('merch');
  const { enabled: phantomLimbEnabled } = useFeatureFlag('phantom_limb_tracker');
  const links = merchEnabled
    ? [...NAV_LINKS.slice(0, 7), { label: 'Merch', to: '/merch' }, NAV_LINKS[7]]
    : NAV_LINKS;
  return (
  <footer className="border-t border-enf-surface-border bg-enf-surface-mid">
    {/* Beta strip */}
    <div className="border-b border-enf-surface-border bg-enf-surface-deep" data-testid="beta-strip">
      <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-sm">
        <div className="text-enf-text-secondary">
          <span className="text-enf-role-info font-semibold mr-2">Enferno is in private beta.</span>
          Built by IronFire Technologies, LLC. Mission-aligned with IronLimb Foundation.
        </div>
        <div className="text-enf-text-muted text-xs">
          Got reactions, suggestions, or things that broke? Tap <span className="text-enf-accent-fg font-semibold">Feedback</span> bottom-left.
        </div>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
      <div className="md:col-span-2">
        <div className="mb-4">
          <span className="enf-brand-pill inline-block">
            <img
              src="/brand/enferno-wordmark-nav@2x.png"
              srcSet="/brand/enferno-wordmark-nav.png 1x, /brand/enferno-wordmark-nav@2x.png 2x"
              alt="Enferno"
              className="h-10 w-auto block"
              loading="lazy"
              decoding="async"
            />
          </span>
        </div>
        <p className="text-enf-text-muted text-sm max-w-md leading-relaxed">
          One app for every discipline. Train smarter. Recover deeper. Bring the heat.
        </p>
        <div className="flex gap-3 mt-5">
          <a aria-label="Instagram" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-md bg-enf-surface-mid hover:bg-enf-accent transition-colors flex items-center justify-center text-enf-text-secondary hover:text-enf-text-primary" href="https://instagram.com/enfernoapp" data-testid="social-instagram"><Instagram className="w-4 h-4" /></a>
          <a aria-label="Facebook" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-md bg-enf-surface-mid hover:bg-enf-accent transition-colors flex items-center justify-center text-enf-text-secondary hover:text-enf-text-primary" href="https://facebook.com/EnfernoApp" data-testid="social-facebook"><Facebook className="w-4 h-4" /></a>
          <a aria-label="X" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-md bg-enf-surface-mid hover:bg-enf-accent transition-colors flex items-center justify-center text-enf-text-secondary hover:text-enf-text-primary" href="https://x.com/EnfernoApp" data-testid="social-x"><XIcon /></a>
          <a aria-label="YouTube" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-md bg-enf-surface-mid hover:bg-enf-accent transition-colors flex items-center justify-center text-enf-text-secondary hover:text-enf-text-primary" href="https://youtube.com/@EnfernoApp" data-testid="social-youtube"><Youtube className="w-4 h-4" /></a>
        </div>
      </div>

      <div>
        <div className="text-enf-text-primary text-sm font-semibold mb-3">Enferno</div>
        <ul className="space-y-2">
          {links.map((l) => (
            <li key={l.label}>
              <Link to={l.to} className="text-enf-text-muted hover:text-enf-text-primary text-sm transition-colors">{l.label}</Link>
            </li>
          ))}
        </ul>
      </div>

      <div data-testid="footer-resources">
        <div className="text-enf-text-primary text-sm font-semibold mb-3">Resources</div>
        <ul className="space-y-2">
          {phantomLimbEnabled && (
          <li>
            <Link to="/adaptive/phantom-limb" className="text-enf-text-muted hover:text-enf-text-primary text-sm transition-colors">
              Phantom-limb awareness
            </Link>
          </li>
          )}
          <li>
            <a href="https://www.amputee-coalition.org/" target="_blank" rel="noopener noreferrer" className="text-enf-text-muted hover:text-enf-text-primary text-sm transition-colors">
              Amputee Coalition
            </a>
          </li>
          <li>
            <a href="https://www.limbsforlife.org/" target="_blank" rel="noopener noreferrer" className="text-enf-text-muted hover:text-enf-text-primary text-sm transition-colors">
              Limbs for Life Foundation
            </a>
          </li>
          <li>
            <a href="https://www.amputee-coalition.org/resources/national-limb-loss-resource-center/" target="_blank" rel="noopener noreferrer" className="text-enf-text-muted hover:text-enf-text-primary text-sm transition-colors">
              National Limb Loss Resource Center
            </a>
          </li>
          <li>
            <a href="https://988lifeline.org/" target="_blank" rel="noopener noreferrer" className="text-enf-text-muted hover:text-enf-text-primary text-sm transition-colors">
              988 Crisis Lifeline
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div className="border-t border-enf-surface-border">
      <div className="max-w-7xl mx-auto px-6 py-5 text-xs text-enf-text-muted flex flex-col md:flex-row items-center justify-between gap-2">
        <div>© 2026 IronFire Technologies, LLC. ENFERNO&trade; is in private beta.</div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <Link to="/legal/privacy" className="hover:text-enf-role-info transition-colors" data-testid="footer-privacy">Privacy</Link>
          <Link to="/legal/terms" className="hover:text-enf-role-info transition-colors" data-testid="footer-terms">Terms</Link>
          <span className="opacity-60">An IronFire Technologies product · Mission-aligned with IronLimb Foundation</span>
        </div>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
